import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DASHBOARD_HRIS, DASHBOARD_OMS, photoFrame, photos } from '../../../content/media';

type Service = {
  id: number;
  anchor: string;
  icon: string;
  title: string;
  description: string;
  highlights: { icon: string; text: string }[];
  image: string;
  image2?: string;
};

const services: Service[] = [
  {
    id: 1,
    anchor: 'it-managed-services',
    icon: 'ri-shield-check-line',
    title: 'IT Managed Services',
    description: 'Comprehensive, proactive management of your entire IT environment. We become your dedicated IT department, ensuring systems stay secure, efficient, and always online.',
    highlights: [
      { icon: 'ri-24-hours-line', text: '24/7 Proactive Monitoring' },
      { icon: 'ri-customer-service-2-line', text: 'Dedicated Help Desk' },
      { icon: 'ri-settings-3-line', text: 'Automated Patch Management' },
      { icon: 'ri-line-chart-line', text: 'Performance Optimization' },
    ],
    image: photos.managedIt,
  },
  {
    id: 2,
    anchor: 'cybersecurity-solutions',
    icon: 'ri-lock-2-line',
    title: 'Cybersecurity Solutions',
    description: 'Multi-layered security strategies that protect your business from evolving threats. From threat detection to compliance, we keep your digital assets safe.',
    highlights: [
      { icon: 'ri-radar-line', text: 'Advanced Threat Detection' },
      { icon: 'ri-file-search-line', text: 'Vulnerability Assessment' },
      { icon: 'ri-award-line', text: 'Compliance Management' },
      { icon: 'ri-shield-flash-line', text: 'Incident Response Planning' },
    ],
    image: photos.cybersecurity,
  },
  {
    id: 3,
    anchor: 'cloud-computing',
    icon: 'ri-cloud-line',
    title: 'Cloud Services & Migration',
    description: 'Accelerate your journey to the cloud with expert planning, migration, and ongoing management. Unlock limitless scalability and reduce infrastructure costs.',
    highlights: [
      { icon: 'ri-server-line', text: 'Seamless Cloud Migration' },
      { icon: 'ri-stack-line', text: 'Multi-Cloud Management' },
      { icon: 'ri-money-dollar-circle-line', text: 'Cost Optimization' },
      { icon: 'ri-global-line', text: 'Remote Access Enablement' },
    ],
    image: photos.cloud,
  },
  {
    id: 4,
    anchor: 'it-consulting',
    icon: 'ri-lightbulb-line',
    title: 'IT Consulting',
    description: 'Strategic technology guidance that aligns your IT investments with business objectives. Our certified consultants drive efficiency, innovation, and digital transformation.',
    highlights: [
      { icon: 'ri-map-2-line', text: 'Technology Roadmapping' },
      { icon: 'ri-organization-chart', text: 'IT Architecture Design' },
      { icon: 'ri-arrow-up-line', text: 'Digital Transformation' },
      { icon: 'ri-scales-line', text: 'Vendor Management' },
    ],
    image: photos.consulting,
  },
  {
    id: 5,
    anchor: 'custom-white-label-saas',
    icon: 'ri-code-s-slash-line',
    title: 'Customizable SaaS',
    description: 'A fully customizable, white-label SaaS platform powering HRIS, OMS, LMS, and industry-specific modules. Deploy enterprise-grade software under your own brand without building from scratch.',
    highlights: [
      { icon: 'ri-shield-keyhole-line', text: 'White-Label HRIS Module' },
      { icon: 'ri-expand-width-line', text: 'OMS & LMS Integration' },
      { icon: 'ri-paint-brush-line', text: 'Full Brand Identity Control' },
      { icon: 'ri-git-branch-line', text: 'Scalable Architecture' },
    ],
    image: DASHBOARD_HRIS,
    image2: DASHBOARD_OMS,
  },
  {
    id: 6,
    anchor: 'disaster-recovery',
    icon: 'ri-database-2-line',
    title: 'Backup & Disaster Recovery',
    description: 'Bulletproof backup strategies and rapid disaster recovery plans that ensure your business can withstand any disruption — natural, technical, or human-caused.',
    highlights: [
      { icon: 'ri-save-3-line', text: 'Automated Backups' },
      { icon: 'ri-speed-line', text: 'Rapid Recovery Times' },
      { icon: 'ri-loop-left-line', text: 'Business Continuity' },
      { icon: 'ri-test-tube-line', text: 'Regular Restore Testing' },
    ],
    image: photos.disasterRecovery,
  },
];

export default function ServicesCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const service = services[active];

  const handleSelect = useCallback((i: number) => {
    setActive(i);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % services.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <section className="bg-white pt-28 lg:pt-40 pb-16">
      <div className="mx-auto px-6 lg:px-10 xl:px-16 mb-8 text-center">
        <span className="eyebrow">What We Do</span>
        <h2 className="text-[26px] lg:text-[34px] font-extrabold text-page-ink tracking-[-0.03em] leading-tight mb-3">
          End-to-End IT Services
        </h2>
        <p className="text-page-muted text-sm max-w-xl mx-auto font-light leading-relaxed">
          From day-to-day managed support to strategic digital transformation, we cover every dimension of your technology needs.
        </p>
      </div>

      <div className="mx-auto px-6 lg:px-10 xl:px-16 mb-8">
        <div className="flex items-center justify-center flex-wrap gap-2" ref={containerRef}>
          {services.map((s, i) => (
            <button
              key={s.id}
              onClick={() => handleSelect(i)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                i === active
                  ? 'bg-[#c97a2b] text-white'
                  : 'bg-black/[0.04] text-page-muted hover:bg-black/[0.07] hover:text-page-ink'
              }`}
            >
              <i className={`${s.icon} text-xs`} />
              <span>{s.title}</span>
            </button>
          ))}
        </div>
      </div>

      <div
        className="mx-auto px-6 lg:px-10 xl:px-16"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="w-12 h-12 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/25 rounded-xl mb-6">
              <i className={`${service.icon} text-[#c97a2b] text-xl`} />
            </div>
            <h3 className="text-3xl lg:text-4xl font-extrabold text-page-ink tracking-[-0.03em] mb-4 leading-tight">
              {service.title}
            </h3>
            <p className="text-page-muted text-[15px] leading-relaxed mb-8 font-light">{service.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {service.highlights.map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-2.5">
                  <i className={`${icon} text-[#c97a2b] text-sm`} />
                  <span className="text-page-ink text-[13px] font-medium">{text}</span>
                </div>
              ))}
            </div>
            <Link to={`/services#${service.anchor}`} className="btn-primary text-sm">
              Explore Service <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>

          <div className={`${photoFrame} relative h-[320px] lg:h-[420px] bg-white`}>
            {service.image2 ? (
              <div className="absolute inset-0 flex flex-col gap-2 p-3">
                <div className="flex-1 relative rounded-2xl overflow-hidden">
                  <img src={service.image} alt={`${service.title} dashboard`} className="absolute inset-0 w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 relative rounded-2xl overflow-hidden">
                  <img src={service.image2} alt={`${service.title} dashboard 2`} className="absolute inset-0 w-full h-full object-cover object-top" />
                </div>
              </div>
            ) : (
              <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover object-center" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
